import { ref, watch } from 'vue';

/**
 * Reactive LocalStorage/SessionStorage.
 *
 * @param {string} key - Storage key
 * @param {any} defaultValue - Default value
 * @returns {Ref} Reactive reference to the stored value
 */
export function useStorage(key, defaultValue) {
  // Try to get the initial value from storage
  let initialValue = defaultValue;
  try {
    const item = localStorage.getItem(key);
    if (item !== null) {
      // Try to parse as JSON first, fallback to raw string
      try {
        initialValue = JSON.parse(item);
      } catch {
        // If JSON parsing fails, treat as raw string
        initialValue = item;
      }
    }
  } catch (error) {
    console.error(`Failed to read from storage key "${key}":`, error);
  }

  // Create reactive ref
  const storedValue = ref(initialValue);

  // Watch for changes and sync to storage
  watch(
    storedValue,
    newValue => {
      try {
        if (newValue === null || newValue === undefined) {
          localStorage.removeItem(key);
        } else {
          // Store strings directly, stringify everything else
          const valueToStore =
            typeof newValue === 'string' ? newValue : JSON.stringify(newValue);
          localStorage.setItem(key, valueToStore);
        }
      } catch (error) {
        console.error(`Failed to write to storage key "${key}":`, error);
      }
    },
    { deep: true }
  );

  // Listen for storage changes from other tabs/windows
  const handleStorageChange = e => {
    if (e.key === key && e.newValue !== null) {
      try {
        // Try to parse as JSON first, fallback to raw string
        try {
          storedValue.value = JSON.parse(e.newValue);
        } catch {
          // If JSON parsing fails, treat as raw string
          storedValue.value = e.newValue;
        }
      } catch (error) {
        console.error(`Failed to parse storage value for key "${key}":`, error);
      }
    }
  };

  // Add storage event listener
  window.addEventListener('storage', handleStorageChange);

  return storedValue;
}
